import type { CSSProperties } from "react";

/**
 * Workspace card grids use a floating footer (no outer tile frame), so row
 * gap is taller than column gap — footers need room without crowding.
 */
export const workspaceCardGridGapClassName = "gap-x-4 gap-y-8";

/**
 * Card-grid density knob. The auto-fill grids below read their column track
 * width from this custom property, falling back to each grid's default when it
 * is unset — so every existing caller renders unchanged. Set it on a grid
 * element (via {@link cardGridTrackStyle}) to make that surface's cards smaller
 * and fit more per row; the auto-fill math packs more, narrower columns and
 * still reflows responsively (no fixed column count). The home browser uses
 * this to render 3-up where the 286px default would only fit 2.
 *
 * NOTE: keep this in sync with the `var(--ws-card-track,…)` literals in the
 * class strings below — Tailwind extracts those from source text and cannot
 * read a JS value, so the variable name has to be spelled out in both places.
 */
export const CARD_GRID_TRACK_VAR = "--ws-card-track";

/** Inline style overriding the card-grid track width for one grid element. */
export const cardGridTrackStyle = (trackPx: number): CSSProperties =>
  ({ [CARD_GRID_TRACK_VAR]: `${trackPx}px` }) as CSSProperties;

/** Canvas / chat recents — 286px tile track (override via `--ws-card-track`). */
export const workspaceCanvasCardGridClassName = `grid grid-cols-[repeat(auto-fill,minmax(min(var(--ws-card-track,286px),100%),1fr))] ${workspaceCardGridGapClassName}`;

/** Chats tab — fixed 286px columns. */
export const workspaceChatCardGridClassName = `grid grid-cols-[repeat(auto-fill,286px)] ${workspaceCardGridGapClassName}`;

/** Files tab — denser 220px track (override via `--ws-card-track`). */
export const workspaceFileCardGridClassName = `grid grid-cols-[repeat(auto-fill,minmax(min(var(--ws-card-track,220px),100%),1fr))] ${workspaceCardGridGapClassName}`;

/** Workspace index tiles — 300px track (override via `--ws-card-track`). */
export const workspaceListCardGridClassName = `grid grid-cols-[repeat(auto-fill,minmax(min(var(--ws-card-track,300px),100%),1fr))] ${workspaceCardGridGapClassName}`;

/** Shared-with-me — slightly wider horizontal rhythm (was gap-5). */
export const workspaceSharedCanvasGridClassName =
  "grid grid-cols-[repeat(auto-fill,minmax(min(286px,100%),1fr))] gap-x-5 gap-y-8";
